/**
 * EventCreationModal Component - Google Calendar-style event creation modal
 *
 * Features:
 * - Simple event creation with calendar selection
 * - Date and time pickers with minute-level granularity
 * - Prepopulated data from time slot clicks
 * - Event type selection
 * - Visibility role configuration
 * - Optional fields (description, location, max participants)
 *
 * @example
 * <EventCreationModal
 *   calendarId="cal-123"
 *   initialDate={new Date()}
 *   initialStartTime={new Date()}
 *   initialEndTime={new Date()}
 *   requiresTimeSelection={false}
 *   onClose={() => {}}
 *   onCreate={() => {}}
 * />
 */

import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { X, Clock, MapPin, Users, AlertCircle, Tag, FileText } from 'lucide-react';
import { useCalendar } from '../../contexts/CalendarContext';
import { useAuth } from '../../contexts/AuthContext';
import calendarService from '../../services/calendarService';
import apiService from '../../services/api';
import { toUTCString, toLocalInputString } from '../../utils/datetime';
import { getDefaultEventColor } from '../../utils/colorResolver';
import Modal from '../Modal';
import Button from '../Button';
import LoadingSpinner from '../LoadingSpinner';
import styles from './EventCreationModal.module.css';

// Training event types that support session templates - moved outside component to avoid re-creating on every render
const TRAINING_EVENT_TYPES = ['training_session', 'skill_session', 'fitness_session', 'practice'];

const EventCreationModal = ({
  calendarId,
  initialDate,
  initialStartTime,
  initialEndTime,
  requiresTimeSelection = false,
  onClose,
  onCreate
}) => {
  const { availableCalendars } = useCalendar();
  const { user } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    calendar_id: calendarId || '',
    title: '',
    description: '',
    event_type: 'practice',
    visibility_roles: ['all'],
    start_time: toLocalInputString(initialStartTime),
    end_time: toLocalInputString(initialEndTime),
    location_name: '',
    max_participants: '',
    color_code: getDefaultEventColor(),
    session_mode: 'template',
    session_template_id: '',
    activities: []
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Session template state
  const [sessionMode, setSessionMode] = useState('template');
  const [sessionTemplates, setSessionTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activityTemplates, setActivityTemplates] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [sessionActivities, setSessionActivities] = useState([]);

  // Event type options
  const eventTypes = [
    { value: 'practice', label: 'Practice' },
    { value: 'training_session', label: 'Training Session' },
    { value: 'skill_session', label: 'Skill Session' },
    { value: 'fitness_session', label: 'Fitness Session' },
    { value: 'game', label: 'Game' },
    { value: 'tournament', label: 'Tournament' },
    { value: 'scrimmage', label: 'Scrimmage' },
    { value: 'friendly_match', label: 'Friendly Match' },
    { value: 'team_meeting', label: 'Team Meeting' },
    { value: 'parent_meeting', label: 'Parent Meeting' },
    { value: 'coach_meeting', label: 'Coach Meeting' },
    { value: 'club_meeting', label: 'Club Meeting' },
    { value: 'camp', label: 'Camp' },
    { value: 'clinic', label: 'Clinic' },
    { value: 'fundraiser', label: 'Fundraiser' },
    { value: 'social_event', label: 'Social Event' },
    { value: 'custom', label: 'Custom Event' }
  ];

  // Visibility role options
  const visibilityRoles = [
    { value: 'all', label: 'All Members' },
    { value: 'staff', label: 'Staff Only' }
  ];

  // Get available calendars for selection
  // Note: can_edit permission implies ability to create and edit events
  const creatableCalendars = availableCalendars.filter(cal => {
    return cal.can_edit === true;
  });

  // Load session templates when event type changes to training
  useEffect(() => {
    const loadSessionTemplates = async () => {
      if (!TRAINING_EVENT_TYPES.includes(formData.event_type)) {
        setSessionTemplates([]);
        setSelectedTemplate(null);
        setSessionActivities([]);
        setFormData(prev => ({ ...prev, session_template_id: '', activities: [] }));
        return;
      }

      if (!user) return;

      try {
        setLoadingTemplates(true);
        let response;

        const calendar = formData.calendar_id
          ? availableCalendars.find(cal => cal.id === formData.calendar_id)
          : null;

        // Determine which endpoint to use based on calendar scope or user role
        // Priority: calendar scope > user's branch > user's club
        let branchId = null;
        let clubId = null;

        if (calendar?.scope === 'branch') {
          // Calendar is branch-scoped, use branch endpoint
          branchId = calendar.scope_id;
        } else if (user.branch_id || user.location_id) {
          // User is branch-level, use branch endpoint
          branchId = user.branch_id || user.location_id;
        } else {
          // Default to club endpoint
          clubId = user.club_id || calendar?.club_id;
        }

        if (branchId) {
          response = await apiService.getBranchSessionTemplates(branchId, {
            active_only: true,
            include_activities: true
          });
        } else if (clubId) {
          response = await apiService.getClubSessionTemplates(clubId, {
            active_only: true,
            include_activities: true
          });
        }

        const filteredTemplates = (response?.templates || []).filter(
          template => template.scope !== 'platform'
        );

        setSessionTemplates(filteredTemplates);
        setLoadingTemplates(false);
      } catch (err) {
        console.error('Failed to load session templates:', err);
        setLoadingTemplates(false);
      }
    };

    loadSessionTemplates();
  }, [formData.event_type, formData.calendar_id, availableCalendars, user]);

  // Load activity templates when in ad-hoc mode
  useEffect(() => {
    const loadActivityTemplates = async () => {
      if (!TRAINING_EVENT_TYPES.includes(formData.event_type) || sessionMode !== 'adhoc') {
        setActivityTemplates([]);
        return;
      }

      if (!user) return;

      try {
        setLoadingActivities(true);
        let response;

        const calendar = formData.calendar_id
          ? availableCalendars.find(cal => cal.id === formData.calendar_id)
          : null;

        // Determine which endpoint to use based on calendar scope or user role
        // Priority: calendar scope > user's branch > user's club
        let branchId = null;
        let clubId = null;

        if (calendar?.scope === 'branch') {
          // Calendar is branch-scoped, use branch endpoint
          branchId = calendar.scope_id;
        } else if (user.branch_id || user.location_id) {
          // User is branch-level, use branch endpoint
          branchId = user.branch_id || user.location_id;
        } else {
          // Default to club endpoint
          clubId = user.club_id || calendar?.club_id;
        }

        if (branchId) {
          response = await apiService.getBranchActivityTemplates(branchId, {
            active_only: true
          });
        } else if (clubId) {
          response = await apiService.getClubActivityTemplates(clubId, {
            active_only: true
          });
        }

        const filteredActivities = (response?.templates || []).filter(
          template => template.scope !== 'platform'
        );

        setActivityTemplates(filteredActivities);
        setLoadingActivities(false);
      } catch (err) {
        console.error('Failed to load activity templates:', err);
        setLoadingActivities(false);
      }
    };

    loadActivityTemplates();
  }, [formData.event_type, sessionMode, formData.calendar_id, availableCalendars, user]);

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Event title is required';
    }

    if (!formData.calendar_id) {
      newErrors.calendar_id = 'Please select a calendar';
    }

    if (!formData.start_time) {
      newErrors.start_time = 'Start time is required';
    }

    if (!formData.end_time) {
      newErrors.end_time = 'End time is required';
    }

    if (formData.start_time && formData.end_time && formData.start_time >= formData.end_time) {
      newErrors.end_time = 'End time must be after start time';
    }

    if (requiresTimeSelection && (!formData.start_time || !formData.end_time)) {
      newErrors.time = 'Please select start and end times';
    }

    if (formData.visibility_roles.length === 0) {
      newErrors.visibility_roles = 'At least one visibility role is required';
    }

    // Validate session data for training events
    if (TRAINING_EVENT_TYPES.includes(formData.event_type)) {
      if (sessionMode === 'template' && !formData.session_template_id) {
        newErrors.session_template_id = 'Session template is required';
      } else if (sessionMode === 'adhoc' && sessionActivities.length === 0) {
        newErrors.activities = 'At least one activity is required for ad-hoc sessions';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle session mode change
  const handleSessionModeChange = (mode) => {
    setSessionMode(mode);
    setFormData(prev => ({ ...prev, session_mode: mode }));
    setSelectedTemplate(null);
    setSessionActivities([]);
    setFormData(prev => ({ ...prev, session_template_id: '', activities: [] }));
  };

  // Handle session template selection
  const handleTemplateSelect = async (templateId) => {
    setFormData(prev => ({ ...prev, session_template_id: templateId }));

    if (!templateId) {
      setSelectedTemplate(null);
      setSessionActivities([]);
      setFormData(prev => ({ ...prev, activities: [] }));
      return;
    }

    try {
      const template = sessionTemplates.find(t => t.id === templateId);
      if (template && template.activities) {
        setSelectedTemplate(template);
        const activities = template.activities.map((activity, index) => ({
          ...activity,
          activity_template_id: activity.activity_template_id,
          planned_duration_mins: activity.duration_mins || activity.planned_duration_mins,
          sequence_order: index + 1,
          activity_name: activity.activity_name
        }));
        setSessionActivities(activities);
        setFormData(prev => ({ ...prev, activities }));
      }
    } catch (err) {
      console.error('Error loading template activities:', err);
    }
  };

  // Handle adding activity in adhoc mode
  const handleAddActivity = (activityId) => {
    const activity = activityTemplates.find(a => a.id === activityId);
    if (!activity) return;

    const newActivity = {
      activity_template_id: activity.id,
      activity_name: activity.name,
      activity_type: activity.activity_type,
      planned_duration_mins: 15,
      sequence_order: sessionActivities.length + 1
    };

    const updatedActivities = [...sessionActivities, newActivity];
    setSessionActivities(updatedActivities);
    setFormData(prev => ({ ...prev, activities: updatedActivities }));
  };

  // Handle removing activity
  const handleRemoveActivity = (index) => {
    const updatedActivities = sessionActivities
      .filter((_, i) => i !== index)
      .map((activity, i) => ({ ...activity, sequence_order: i + 1 }));
    setSessionActivities(updatedActivities);
    setFormData(prev => ({ ...prev, activities: updatedActivities }));
  };

  // Handle activity duration change
  const handleActivityDurationChange = (index, duration) => {
    const updatedActivities = sessionActivities.map((activity, i) => 
      i === index ? { ...activity, planned_duration_mins: parseInt(duration) || 0 } : activity
    );
    setSessionActivities(updatedActivities);
    setFormData(prev => ({ ...prev, activities: updatedActivities }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent duplicate submissions
    if (loading) {
      return;
    }

    if (!validate()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const eventData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        event_type: formData.event_type,
        visibility_roles: formData.visibility_roles,
        start_time: toUTCString(formData.start_time),
        end_time: toUTCString(formData.end_time),
        location_name: formData.location_name.trim() || null,
        max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
        color_code: formData.color_code,
        all_day: false
      };

      // Create the event first
      const eventResponse = await calendarService.createEvent(formData.calendar_id, eventData);
      const eventId = eventResponse.event_id || eventResponse.id;

      // If it's a training event, create the training session
      if (TRAINING_EVENT_TYPES.includes(formData.event_type) && (formData.session_template_id || formData.activities?.length > 0)) {
        const calendar = availableCalendars.find(cal => cal.id === formData.calendar_id);
        let branchId = null;

        if (calendar?.scope === 'branch') {
          branchId = calendar.scope_id;
        } else if (user?.branch_id) {
          branchId = user.branch_id;
        } else if (user?.location_id) {
          branchId = user.location_id;
        }

        if (!branchId) {
          throw new Error(
            'Training sessions require a branch. Please select a branch-level calendar or contact your administrator.'
          );
        }

        const trainingSessionData = {
          branch_id: branchId,
          scheduled_date: toUTCString(formData.start_time),
          event_id: eventId,
        };

        if (sessionMode === 'template') {
          trainingSessionData.session_template_id = formData.session_template_id;
        }

        trainingSessionData.activities = formData.activities.map(activity => ({
          activity_template_id: activity.activity_template_id,
          planned_duration_mins: parseInt(activity.planned_duration_mins),
          sequence_order: activity.sequence_order
        }));

        await apiService.createTrainingSession(branchId, trainingSessionData);
      }

      // Success - call onCreate callback (which will close modal and reload events)
      if (onCreate) {
        onCreate();
      }

      // Note: Don't set loading to false here as the modal will close
    } catch (err) {
      console.error('Error creating event:', err);
      setError(err.message || 'Failed to create event. Please try again.');
      setLoading(false);
    }
  };

  // Handle field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle visibility role toggle
  const handleVisibilityToggle = (role) => {
    setFormData(prev => {
      const currentRoles = prev.visibility_roles;
      const newRoles = currentRoles.includes(role)
        ? currentRoles.filter(r => r !== role)
        : [...currentRoles, role];

      return {
        ...prev,
        visibility_roles: newRoles.length > 0 ? newRoles : ['all'] // Always have at least one
      };
    });
  };

  // Handle keyboard events
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Create Event"
      size="lg"
    >
      <form onSubmit={handleSubmit} className={styles.modalContent}>
          {error && (
            <div className={styles.errorBanner}>
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {requiresTimeSelection && (
            <div className={styles.infoBanner}>
              <Clock size={16} />
              <span>Please select start and end times for this event</span>
            </div>
          )}

          {/* Calendar Selection */}
          {creatableCalendars.length > 1 && (
            <div className={styles.formGroup}>
              <label htmlFor="calendar_id">
                Calendar <span className={styles.required}>*</span>
              </label>
              <select
                id="calendar_id"
                value={formData.calendar_id}
                onChange={(e) => handleChange('calendar_id', e.target.value)}
                className={errors.calendar_id ? styles.error : ''}
                disabled={creatableCalendars.length === 1}
              >
                <option value="">Select a calendar</option>
                {creatableCalendars.map(cal => (
                  <option key={cal.id} value={cal.id}>
                    {cal.name}
                  </option>
                ))}
              </select>
              {errors.calendar_id && <span className={styles.errorText}>{errors.calendar_id}</span>}
            </div>
          )}

          {/* Event Title */}
          <div className={styles.formGroup}>
            <label htmlFor="title">
              Event Title <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter event title"
              className={errors.title ? styles.error : ''}
              autoFocus
            />
            {errors.title && <span className={styles.errorText}>{errors.title}</span>}
          </div>

          {/* Event Type */}
          <div className={styles.formGroup}>
            <label htmlFor="event_type">Event Type</label>
            <select
              id="event_type"
              value={formData.event_type}
              onChange={(e) => handleChange('event_type', e.target.value)}
            >
              {eventTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Session Template Selection - Only for training event types */}
          {TRAINING_EVENT_TYPES.includes(formData.event_type) && (
            <div className={styles.formGroup}>
              <label htmlFor="session_template_id">
                <FileText size={16} className={styles.labelIcon} />
                Session Template {sessionMode === 'template' && <span className={styles.required}>*</span>}
              </label>
              <select
                id="session_template_id"
                value={formData.session_template_id}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className={errors.session_template_id ? styles.error : ''}
                disabled={loadingTemplates}
              >
                <option value="">
                  {loadingTemplates ? 'Loading templates...' : 'Select a session template (optional)'}
                </option>
                {sessionTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
              {errors.session_template_id && (
                <span className={styles.errorText}>{errors.session_template_id}</span>
              )}
            </div>
          )}

          {/* Date and Time */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="start_time">
                Start Time <span className={styles.required}>*</span>
              </label>
              <input
                type="datetime-local"
                id="start_time"
                value={formData.start_time}
                onChange={(e) => handleChange('start_time', e.target.value)}
                className={errors.start_time ? styles.error : ''}
              />
              {errors.start_time && <span className={styles.errorText}>{errors.start_time}</span>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="end_time">
                End Time <span className={styles.required}>*</span>
              </label>
              <input
                type="datetime-local"
                id="end_time"
                value={formData.end_time}
                onChange={(e) => handleChange('end_time', e.target.value)}
                className={errors.end_time ? styles.error : ''}
              />
              {errors.end_time && <span className={styles.errorText}>{errors.end_time}</span>}
            </div>
          </div>

          {/* Description */}
          <div className={styles.formGroup}>
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Add event description (optional)"
              rows={2}
            />
          </div>

          {/* Location */}
          <div className={styles.formGroup}>
            <label htmlFor="location_name">
              <MapPin size={16} className={styles.labelIcon} />
              Location
            </label>
            <input
              type="text"
              id="location_name"
              value={formData.location_name}
              onChange={(e) => handleChange('location_name', e.target.value)}
              placeholder="Add location (optional)"
            />
          </div>

          {/* Max Participants */}
          <div className={styles.formGroup}>
            <label htmlFor="max_participants">
              <Users size={16} className={styles.labelIcon} />
              Max Participants
            </label>
            <input
              type="number"
              id="max_participants"
              value={formData.max_participants}
              onChange={(e) => handleChange('max_participants', e.target.value)}
              placeholder="No limit"
              min="1"
            />
          </div>

          {/* Visibility Roles */}
          <div className={styles.formGroup}>
            <label>
              Visibility <span className={styles.required}>*</span>
            </label>
            <div className={styles.checkboxGroup}>
              {visibilityRoles.map(role => (
                <label key={role.value} className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.visibility_roles.includes(role.value)}
                    onChange={() => handleVisibilityToggle(role.value)}
                  />
                  <span>{role.label}</span>
                </label>
              ))}
            </div>
            {errors.visibility_roles && (
              <span className={styles.errorText}>{errors.visibility_roles}</span>
            )}
          </div>

          {/* Color */}
          <div className={styles.formGroup}>
            <label htmlFor="color_code">Event Color</label>
            <input
              type="color"
              id="color_code"
              value={formData.color_code}
              onChange={(e) => handleChange('color_code', e.target.value)}
              className={styles.colorPicker}
            />
          </div>

          {/* Form Actions */}
          <div className={styles.modalActions}>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" />
                  Creating...
                </>
              ) : (
                'Create Event'
              )}
            </Button>
          </div>
        </form>
    </Modal>
  );
};

export default EventCreationModal;
